import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import AuthLayout from "@/components/AuthLayout";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState(token ? "verifying" : "missing");
  const [error, setError] = useState("");
  const { refreshUser } = useAuth();

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        setStatus("ok");
        await refreshUser();
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || e.message);
        setStatus("error");
      }
    })();
  }, [token, refreshUser]);

  return (
    <AuthLayout eyebrow="Email verification" title={status === "ok" ? "You're verified" : "Verifying your email"}>
      {status === "verifying" && (
        <div className="flex items-center gap-3 text-nexa-mute" data-testid="verify-loading">
          <Loader2 className="w-5 h-5 animate-spin" /> Checking your link…
        </div>
      )}
      {status === "ok" && (
        <>
          <div data-testid="verify-success" className="flex items-center gap-3 p-4 rounded-xl bg-[#22C55E]/8 border border-[#22C55E]/20 text-[#166534]">
            <CheckCircle2 className="w-5 h-5" /> <span className="text-[15px]">Your email is confirmed. NEXA is ready.</span>
          </div>
          <Link to="/dashboard" data-testid="verify-continue-btn" className="mt-6 lux-btn-primary w-full flex items-center justify-center">
            Continue to dashboard
          </Link>
        </>
      )}
      {status === "error" && (
        <>
          <div data-testid="verify-error" className="flex items-center gap-3 p-4 rounded-xl bg-[#EF4444]/8 border border-[#EF4444]/20 text-[#991B1B]">
            <XCircle className="w-5 h-5" /> <span className="text-[15px]">{error}</span>
          </div>
          <Link to="/login" className="mt-6 lux-btn-secondary w-full flex items-center justify-center">Back to sign in</Link>
        </>
      )}
      {status === "missing" && (
        <div className="text-[15px] text-nexa-mute">This link is invalid. <Link to="/login" className="lux-link">Return to sign in</Link>.</div>
      )}
    </AuthLayout>
  );
}
